import { Deferred, Effect } from 'effect'
import { FlateErrorCode, unzip, Zip, ZipPassThrough } from 'fflate'
import { ArchiveGenerationError, FileReadError, InvalidArchiveError } from './archive-errors'
import { FileEntry } from './file-entry'

// ZIP's DOS timestamp format starts at 1980. Use its epoch when no source time exists.
export const ZIP_EPOCH_MS = Date.UTC(1980, 0, 1)

export function zipEntries(
  entries: FileEntry[],
  filename: string
): Effect.Effect<File, FileReadError | ArchiveGenerationError> {
  return Effect.scoped(
    Effect.gen(function* () {
      const completed = yield* Deferred.make<File, ArchiveGenerationError>()
      const chunks: Uint8Array[] = []
      let totalSize = 0
      let settled = false

      const zip = new Zip((error, chunk, final) => {
        if (settled) return

        if (error) {
          settled = true
          zip.terminate()
          Deferred.doneUnsafe(
            completed,
            isArchiveMetadataError(error.code, FlateErrorCode)
              ? Effect.fail(new ArchiveGenerationError({ cause: error }))
              : Effect.die(error)
          )
          return
        }

        chunks.push(chunk)
        totalSize += chunk.length
        if (!final) return

        settled = true
        Deferred.doneUnsafe(
          completed,
          Effect.sync(() => {
            const merged = new Uint8Array(totalSize)
            let offset = 0
            for (const piece of chunks) {
              merged.set(piece, offset)
              offset += piece.length
            }

            return new File([merged], filename, {
              lastModified: ZIP_EPOCH_MS,
              type: 'application/zip'
            })
          })
        )
      })

      yield* Effect.addFinalizer(() =>
        Effect.sync(() => {
          if (!settled) {
            settled = true
            zip.terminate()
          }
        })
      )

      for (const entry of entries) {
        const stream = new ZipPassThrough(entry.path)
        const { lastModified } = entry.file
        if (typeof lastModified === 'number') {
          stream.mtime = new Date(lastModified)
        }

        zip.add(stream)
        if (settled) return yield* Deferred.await(completed)

        const contents = yield* entry.readBytes
        stream.push(contents, true)
        if (settled) return yield* Deferred.await(completed)
      }

      zip.end()
      return yield* Deferred.await(completed)
    })
  )
}

export function unzipEntries(
  zipBytes: Uint8Array
): Effect.Effect<FileEntry[], InvalidArchiveError> {
  return Effect.callback<FileEntry[], InvalidArchiveError>((resume) => {
    const terminate = unzip(zipBytes, (error, files) => {
      if (error) {
        resume(
          isInvalidArchiveError(error.code, FlateErrorCode)
            ? Effect.fail(new InvalidArchiveError({ cause: error }))
            : Effect.die(error)
        )
        return
      }

      resume(
        Effect.sync(() => {
          const entries = Object.entries(files)
            .filter(([path]) => path.length > 0 && !path.endsWith('/'))
            .map(([path, contents]) => {
              const file = new File([contents as unknown as BlobPart], getFileName(path), {
                lastModified: ZIP_EPOCH_MS,
                type: 'application/octet-stream'
              })
              return { path, file, readBytes: Effect.succeed(contents) }
            })

          entries.sort((a, b) => a.path.localeCompare(b.path))
          return entries
        })
      )
    })

    return Effect.sync(terminate)
  })
}

type FlateErrorCodes = typeof import('fflate').FlateErrorCode

function isInvalidArchiveError(code: number, codes: FlateErrorCodes) {
  return (
    code == codes.UnexpectedEOF ||
    code == codes.InvalidBlockType ||
    code == codes.InvalidLengthLiteral ||
    code == codes.InvalidDistance ||
    code == codes.InvalidHeader ||
    code == codes.InvalidUTF8 ||
    code == codes.InvalidZipData ||
    code == codes.UnknownCompressionMethod
  )
}

function isArchiveMetadataError(code: number, codes: FlateErrorCodes) {
  return (
    code == codes.ExtraFieldTooLong || code == codes.InvalidDate || code == codes.FilenameTooLong
  )
}

function getFileName(path: string): string {
  const slashIndex = path.lastIndexOf('/')
  if (slashIndex === -1) {
    return path
  }

  return path.slice(slashIndex + 1)
}
